import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownComponent } from 'ngx-markdown';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { DOMAIN } from '../constants/app.constants';
import { HelpCenterAiState } from '../help-center/help-center.types';
import { HelpCenterContextService } from '../services/help-center-context.service';
import { KBService } from '../services/kb.service';

@Component({
  selector: 'app-ask-ai',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzSpinModule,
    MarkdownComponent,
    TranslateModule
  ],
  template: `
    <div class="ask-ai">
      <div class="ask-ai__composer">
        <textarea
          [(ngModel)]="question"
          rows="4"
          nz-input
          [placeholder]="'ASK_AI.PLACEHOLDER' | translate"
          (keydown)="onKeyDown($event)"
        ></textarea>
        <div class="ask-ai__actions">
          <button nz-button nzType="default" *ngIf="question" (click)="clearQuestion()">
            {{ 'ASK_AI.CLEAR' | translate }}
          </button>
          <button nz-button nzType="primary" [disabled]="!question.trim() || loading" [nzLoading]="loading" (click)="submit()">
            {{ 'ASK_AI.ASK' | translate }}
          </button>
        </div>
      </div>

      <div *ngIf="!question && !loading && !answer" class="ask-ai__examples">
        <p>{{ 'ASK_AI.EXAMPLES_TITLE' | translate }}</p>
        <button
          *ngFor="let example of exampleQuestions"
          nz-button
          nzType="default"
          class="ask-ai__example"
          (click)="askExample(example)"
        >
          {{ example }}
        </button>
      </div>

      <div *ngIf="loading" class="ask-ai__loading">
        <nz-spin nzSimple></nz-spin>
      </div>

      <div *ngIf="answer" class="ask-ai__answer">
        <strong>{{ 'ASK_AI.ANSWER' | translate }}</strong>
        <markdown [data]="answer"></markdown>

        <div *ngIf="sources.length" class="ask-ai__sources">
          <strong>{{ 'ASK_AI.SOURCES' | translate }}</strong>
          <a *ngFor="let source of sources" [href]="DOMAIN + '/docs/' + source.slug" target="_blank" rel="noopener noreferrer">
            {{ source.title }}
          </a>
        </div>

        <div class="ask-ai__feedback" *ngIf="!loading">
          <span>{{ 'ASK_AI.FEEDBACK_PROMPT' | translate }}</span>
          <button nz-button nzType="text" (click)="submitFeedback('up')" [disabled]="feedbackLoading || selectedFeedback === 'up'">
            <span nz-icon nzType="like"></span>
          </button>
          <button nz-button nzType="text" (click)="submitFeedback('down')" [disabled]="feedbackLoading || selectedFeedback === 'down'">
            <span nz-icon nzType="dislike"></span>
          </button>
        </div>

        <div class="ask-ai__escalation">
          <strong>{{ 'ASK_AI.NEED_MORE_HELP' | translate }}</strong>
          <div class="ask-ai__escalation-actions">
            <button nz-button nzType="default" (click)="escalate.emit('support')">
              {{ 'ASK_AI.CONTACT_SUPPORT' | translate }}
            </button>
            <button nz-button nzType="default" (click)="escalate.emit('bug')">
              {{ 'ASK_AI.REPORT_BUG' | translate }}
            </button>
          </div>
          <p *ngIf="selectedFeedback === 'down'">{{ 'ASK_AI.NOT_HELPFUL_HINT' | translate }}</p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .ask-ai {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .ask-ai__composer {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .ask-ai__actions,
    .ask-ai__feedback,
    .ask-ai__escalation-actions {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      flex-wrap: wrap;
    }

    .ask-ai__examples {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .ask-ai__example {
      text-align: left;
      justify-content: flex-start;
    }

    .ask-ai__loading {
      display: flex;
      justify-content: center;
      padding: 1rem 0;
    }

    .ask-ai__answer,
    .ask-ai__sources,
    .ask-ai__escalation {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .ask-ai__sources a {
      color: #1677ff;
    }

    .ask-ai__escalation p {
      margin: 0;
      color: #64748b;
    }
  `]
})
export class AskAiComponent {
  @Output() interactionChange = new EventEmitter<HelpCenterAiState | null>();
  @Output() escalate = new EventEmitter<'support' | 'bug'>();

  question = '';
  answer = '';
  loading = false;
  feedbackLoading = false;
  selectedFeedback: 'up' | 'down' | null = null;
  interactionId = '';
  sources: { title: string; slug: string }[] = [];

  constructor(
    private readonly kbService: KBService,
    private readonly translate: TranslateService,
    private readonly message: NzMessageService,
    private readonly contextService: HelpCenterContextService
  ) {}

  get exampleQuestions(): string[] {
    return [
      this.translate.instant('ASK_AI.EXAMPLE_1'),
      this.translate.instant('ASK_AI.EXAMPLE_2'),
      this.translate.instant('ASK_AI.EXAMPLE_3')
    ];
  }

  clearQuestion(): void {
    this.question = '';
  }

  askExample(example: string): void {
    this.question = example;
    this.submit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.submit();
    }
  }

  submit(): void {
    const normalizedQuestion = this.question.trim();
    if (!normalizedQuestion || this.loading) {
      return;
    }

    this.loading = true;
    this.answer = '';
    this.sources = [];
    this.interactionId = '';
    this.selectedFeedback = null;
    this.interactionChange.emit(null);

    this.kbService.askAI({
      question: normalizedQuestion,
      language: this.translate.currentLang || 'de',
      context: this.contextService.getContext()
    }).subscribe({
      next: (res) => {
        this.answer = res.answer;
        this.sources = res.sources || [];
        this.interactionId = res.interactionId || '';
        this.emitInteractionState();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.message.error(this.translate.instant('ASK_AI.ERROR'));
      }
    });
  }

  submitFeedback(feedback: 'up' | 'down'): void {
    if (!this.answer || this.feedbackLoading) {
      return;
    }

    this.feedbackLoading = true;
    this.kbService.sendFeedback({
      interactionId: this.interactionId,
      question: this.question.trim(),
      answer: this.answer,
      feedback,
      sources: this.sources,
      context: this.contextService.getContext(),
      language: this.translate.currentLang || 'de'
    }).subscribe({
      next: () => {
        this.selectedFeedback = feedback;
        this.emitInteractionState();
        this.feedbackLoading = false;
        this.message.success(this.translate.instant('ASK_AI.FEEDBACK_THANKS'));
      },
      error: () => {
        this.feedbackLoading = false;
        this.message.error(this.translate.instant('ASK_AI.FEEDBACK_ERROR'));
      }
    });
  }

  private emitInteractionState(): void {
    this.interactionChange.emit({
      interactionId: this.interactionId || undefined,
      question: this.question.trim(),
      answer: this.answer,
      feedback: this.selectedFeedback,
      sources: this.sources
    });
  }

  protected readonly DOMAIN = DOMAIN;
}
